import { TableSkeleton } from "components/PortalLoading";
import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
	BsCheckCircle,
	BsCloudUpload,
	BsDownload,
	BsEnvelope,
	BsFileEarmarkExcel,
} from "react-icons/bs";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { ConfirmPopup } from "primereact/confirmpopup";
import toast from "react-hot-toast";
import useListBulkImports from "api/BulkBookImport/useListBulkImports";
import useCancelBulkImport from "api/BulkBookImport/useCancelBulkImport";
import useDeleteBulkImport from "api/BulkBookImport/useDeleteBulkImport";
import useDownloadBulkImportReport from "api/BulkBookImport/useDownloadBulkImportReport";
import useBulkImportLimits from "api/BulkBookImport/useBulkImportLimits";
import StatusBanner from "../StatusBanner";
import CancelImportDialog from "../CancelImportDialog";
import ImportLimitsNotice from "../ImportLimitsNotice";

const PROCESS_STEPS = [
	{
		n: 1,
		title: "Download your book list",
		body: "An Excel list of books missing a cover or PDF.",
		Icon: BsDownload,
	},
	{
		n: 2,
		title: "Fill it in and upload it back",
		body: "Type the cover and PDF file names next to each book.",
		Icon: BsFileEarmarkExcel,
	},
	{
		n: 3,
		title: "Upload the covers and PDFs",
		body: "The files you named in the list.",
		Icon: BsCloudUpload,
	},
	{
		n: 4,
		title: "Submit and check your email",
		body: "You can close the page; we email the report.",
		Icon: BsEnvelope,
	},
];

const ACTIVE_STATUSES = [
	"draft",
	"uploading",
	"validating",
	"validation_failed",
	"ready",
	"processing",
];

const CANCELLABLE = [
	"draft",
	"uploading",
	"validating",
	"validation_failed",
	"ready",
	"processing",
];

const DELETABLE = [
	"draft",
	"uploading",
	"validation_failed",
	"ready",
	"cancelled",
	"failed",
	"completed",
	"completed_with_errors",
];

const REPORTABLE = [
	"validation_failed",
	"completed",
	"completed_with_errors",
	"failed",
	"cancelled",
];

const RECENT_FINISHED = [
	"completed",
	"completed_with_errors",
	"failed",
	"cancelled",
];

const formatWhen = (iso) => {
	if (!iso) return "";
	try {
		return new Date(iso).toLocaleString();
	} catch {
		return iso;
	}
};

const ImportActionRow = ({ item }) => {
	const history = useHistory();
	const cancel = useCancelBulkImport();
	const remove = useDeleteBulkImport();
	const download = useDownloadBulkImportReport();
	const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
	const [deletePopupOpen, setDeletePopupOpen] = useState(false);
	const id = item.id;
	const deleteButtonId = `bulk-import-delete-${id}`;
	const busy =
		(cancel.isLoading && cancel.variables === id) ||
		(remove.isLoading && remove.variables === id) ||
		(download.isLoading && download.variables === id);
	const canCancel = CANCELLABLE.includes(item.status);
	const canDelete = DELETABLE.includes(item.status);
	const canDownload = REPORTABLE.includes(item.status);

	return (
		<li className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="text-sm font-semibold text-gray-900">
						<span className="capitalize">
							{(item.status || "").replace(/_/g, " ")}
						</span>
					</p>
					<p className="mt-0.5 text-xs text-gray-500">
						{formatWhen(
							item.completed_at || item.started_at || item.confirmed_at
						) || "Not started yet"}
						{item.counts?.total ? ` · ${item.counts.total} row(s)` : ""}
					</p>
					<p className="mt-1 truncate font-mono text-xs text-gray-400">
						{id}
					</p>
				</div>
				<div className="flex flex-wrap gap-2">
					<button
						type="button"
						className="rounded-lg bg-akgreen px-3 py-1.5 text-xs font-semibold text-white hover:bg-akgreener disabled:opacity-50"
						disabled={busy}
						onClick={() => history.push(`/bulk-book-imports/${id}`)}
					>
						Open
					</button>
					{canDownload ? (
						<button
							type="button"
							className="inline-flex items-center gap-1 rounded-lg border border-akgreen border-opacity-30 px-3 py-1.5 text-xs font-medium text-akgreenest hover:bg-akgreen bg-opacity-5 disabled:opacity-50"
							disabled={busy}
							onClick={() => download.mutate(id)}
						>
							<BsDownload className="h-3 w-3" aria-hidden />
							{busy && download.variables === id
								? "Downloading…"
								: "Report"}
						</button>
					) : null}
					{canCancel ? (
						<button
							type="button"
							className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
							disabled={busy}
							onClick={() => setCancelDialogOpen(true)}
						>
							{busy && cancel.variables === id ? "Cancelling…" : "Cancel"}
						</button>
					) : null}
					{canDelete ? (
						<button
							type="button"
							id={deleteButtonId}
							className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
							disabled={busy}
							onClick={() => setDeletePopupOpen(true)}
						>
							{busy && remove.variables === id ? "Deleting…" : "Delete"}
						</button>
					) : null}
				</div>
			</div>

			<CancelImportDialog
				visible={cancelDialogOpen}
				onHide={() => setCancelDialogOpen(false)}
				busy={cancel.isLoading && cancel.variables === id}
				onConfirm={() => {
					cancel.mutate(id, {
						onSuccess: () => setCancelDialogOpen(false),
					});
				}}
			/>

			{canDelete ? (
				<ConfirmPopup
					target={document.getElementById(deleteButtonId)}
					className="font-sans text-sm"
					visible={deletePopupOpen}
					onHide={() => setDeletePopupOpen(false)}
					icon="pi pi-exclamation-triangle"
					message={
						<div className="max-w-xs space-y-1.5 p-1">
							<p>
								Delete this import permanently? Staged files for this
								batch will be removed.
							</p>
							<p className="text-red-600">This cannot be undone.</p>
						</div>
					}
					accept={() => {
						remove.mutate(id, {
							onSuccess: () => setDeletePopupOpen(false),
						});
					}}
					reject={() => setDeletePopupOpen(false)}
				/>
			) : null}
		</li>
	);
};

const ActiveImportsPanel = ({ imports, isLoading, maxActive = 2 }) => {
	if (isLoading) {
		return (
			<TableSkeleton label="Checking for active imports…" columns={["Batch", "Files", "Status"]} rows={2} footer={false} />
		);
	}

	if (!imports.length) return null;

	return (
		<div className="mx-auto w-full max-w-lg space-y-3 text-left">
			<StatusBanner
				tone="warning"
				title="Active imports are blocking a new start"
			>
				You can have at most {maxActive} active batch
				{maxActive === 1 ? "" : "es"}. Open one, or cancel/delete a leftover
				draft to free a slot. Download the Excel report when validation has
				run.
			</StatusBanner>
			<ul className="space-y-2">
				{imports.map((item) => (
					<ImportActionRow key={item.id} item={item} />
				))}
			</ul>
		</div>
	);
};

const RecentImportsPanel = ({ imports }) => {
	if (!imports.length) return null;

	return (
		<div className="mx-auto w-full max-w-lg space-y-3 text-left">
			<p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
				Recent finished imports
			</p>
			<ul className="space-y-2">
				{imports.map((item) => (
					<ImportActionRow key={item.id} item={item} />
				))}
			</ul>
		</div>
	);
};

const SelectModeStep = ({ onSelectAttach, isStarting }) => {
	const { data, isLoading } = useListBulkImports({ per_page: 20 });
	const { data: limits } = useBulkImportLimits();
	const maxActive = limits?.max_active_per_publisher ?? 2;
	const rows = data?.data || [];
	const activeImports = useMemo(
		() => rows.filter((row) => ACTIVE_STATUSES.includes(row.status)),
		[rows]
	);
	const recentFinished = useMemo(
		() =>
			rows
				.filter((row) => RECENT_FINISHED.includes(row.status))
				.slice(0, 5),
		[rows]
	);
	const atLimit = activeImports.length >= maxActive;

	return (
		<div className="animate-fade-in-up space-y-8">
			<div className="mx-auto max-w-xl space-y-3 text-center">
				<div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-akgreen bg-opacity-10 text-akgreen">
					<BsCloudUpload className="h-7 w-7" aria-hidden />
				</div>
				<h2 className="text-2xl font-semibold tracking-tight text-akgreenest">
					Attach files to existing books
				</h2>
				<p className="text-sm leading-relaxed text-gray-600">
					For books that already exist and only need missing covers or
					PDFs. Existing media is never overwritten. Single-book upload
					and CSV metadata import stay in the create menu.
				</p>
			</div>

			<ol className="relative mx-auto max-w-lg space-y-0">
				{PROCESS_STEPS.map((step, index) => {
					const Icon = step.Icon;
					return (
						<li key={step.n} className="relative flex gap-4 pb-6 last:pb-0">
							{index < PROCESS_STEPS.length - 1 ? (
								<span
									className="absolute left-5 top-10 portal-step-line w-px bg-akgreen bg-opacity-20"
									aria-hidden
								/>
							) : null}
							<span className="relative z-10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-akgreen text-white shadow-sm">
								<Icon className="h-4 w-4" aria-hidden />
							</span>
							<div className="pt-1.5">
								<p className="font-semibold text-gray-900">
									<span className="mr-2 text-akgreen">{step.n}.</span>
									{step.title}
								</p>
								<p className="mt-0.5 text-sm text-gray-600">{step.body}</p>
							</div>
						</li>
					);
				})}
			</ol>

			<div className="mx-auto w-full max-w-lg">
				<ImportLimitsNotice limits={limits} />
			</div>

			<ActiveImportsPanel
				imports={activeImports}
				isLoading={isLoading}
				maxActive={maxActive}
			/>
			<RecentImportsPanel imports={recentFinished} />

			<div className="flex flex-col items-center gap-3 pt-2">
				<button
					type="button"
					onClick={() => {
						if (isStarting) return;
						if (atLimit) {
							toast.error(
								`You already have ${maxActive} active import${
									maxActive === 1 ? "" : "s"
								}. Cancel or delete one above to start another.`
							);
							return;
						}
						onSelectAttach();
					}}
					disabled={isStarting}
					aria-disabled={isStarting || atLimit}
					className={`inline-flex items-center justify-center gap-2 rounded-xl px-8 py-3 text-sm font-semibold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-akgreen focus:ring-offset-2 ${
						isStarting
							? "cursor-wait bg-akgreen text-white opacity-50"
							: atLimit
							? "cursor-pointer border border-gray-300 bg-gray-200 text-gray-600 hover:bg-gray-300"
							: "bg-akgreen text-white hover:bg-akgreener"
					}`}
				>
					{isStarting ? (
						<>
							<AiOutlineLoading3Quarters
								className="h-4 w-4 animate-spin"
								aria-hidden
							/>
							Starting…
						</>
					) : (
						<>
							Start — download my book list
							<BsCheckCircle className="h-4 w-4 opacity-80" aria-hidden />
						</>
					)}
				</button>
				<p
					className={`text-xs ${
						atLimit ? "font-medium text-amber-800" : "text-gray-500"
					}`}
				>
					{atLimit
						? `Cancel or delete an active import above to start a new one (limit ${maxActive}).`
						: "Existing covers and PDFs are never replaced."}
				</p>
			</div>
		</div>
	);
};

export default SelectModeStep;
